import { difference, has, indexBy, isEmpty, isNil, pluck, propOr, reject } from '../../../utils/fp';
import { parseDataquery, selectAutomatedSelections } from '../selection';
import { getDefaultSelection } from '../../search/sdmx-constraints-bridge';
import * as rules from '../../rules';

const ascending = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

const getJoinValuesIds =
  ({ current, valIds }) =>
  dimension => {
    if (isEmpty(valIds)) return '';
    const dimensionIndexedByValueId = indexBy(value => value.id, propOr([], 'values', dimension));
    const sanitize = list => list.filter(id => has(id, dimensionIndexedByValueId));
    const tokens = isNil(current) ? current : current.split('+');
    const validCurrent = sanitize(tokens.filter(token => !isEmpty(token)));
    const validNew = sanitize(valIds);
    const symmetric = [
      ...difference(validNew, validCurrent),
      ...difference(validCurrent, validNew),
    ];
    return symmetric.sort(ascending).join('+');
  };

export const updateDataquery = (dimensions, dataquery, valIdsIndexByDimId) => {
  const splitDataquery = dataquery.split('.');

  return dimensions
    .map((dim, index) => {
      const current = splitDataquery[index];
      const dimId = dim.id;
      const valIds = valIdsIndexByDimId?.[dimId];
      if (rules.isFrequencyDimension(dim)) {
        if (isNil(valIds) || isEmpty(valIds)) return current;
        // frequency valIds take only the first one; replace current if it is a valid value
        const found = (dim.values || []).find(value => value.id === valIds[0]);
        return found ? valIds[0] : current;
      }
      // if not frequency and undefined or empty valIdsIndexByDimId, then erase
      if (isNil(valIdsIndexByDimId) || isEmpty(valIdsIndexByDimId)) return '';
      return has(dimId, valIdsIndexByDimId) ? getJoinValuesIds({ current, valIds })(dim) : current; // keep current
    })
    .join('.');
};

export const computeDataquery = (dimensions, selection) =>
  dimensions
    .map(dim => {
      const dimSelection = propOr([], dim.id, selection);
      const valIds = new Set(pluck('id', dim.values || []));
      return dimSelection
        .filter(id => valIds.has(id))
        .sort()
        .join('+');
    })
    .join('.');

const filterByAnnotationMaybe = hiddenValuesAnnotation => dimensions =>
  dimensions.filter(dim => {
    const values = propOr([], 'values', dim);
    return hiddenValuesAnnotation[dim.id]
      ? !isEmpty(difference(hiddenValuesAnnotation[dim.id], pluck('id', values)))
      : true;
  });

const computeDefaultSelection = ({
  hiddenValuesAnnotation,
  constraints,
  highlightedContraints,
  contentConstraints = {},
  hasDataAvailability,
  dimensions,
  selection,
  hierarchies,
}) => {
  const hasValidDataAvailability = hasDataAvailability && !isEmpty(contentConstraints || []);
  const filtered = filterByAnnotationMaybe(hiddenValuesAnnotation)(dimensions);
  const defaultSelection = getDefaultSelection(
    filtered,
    selection,
    constraints,
    highlightedContraints,
    hierarchies,
    hasValidDataAvailability ? contentConstraints : {},
  );
  return reject(isEmpty, defaultSelection);
};

export const dataqueryComputor = ({
  currentDataquery,
  dimensions,
  automatedSelections,
  hierarchies,
  hiddenValuesAnnotation,
  constraints, // facet selection
  highlightedContraints, // highlights from search
  contentConstraints, // actual contentConstraint from structure
  hasDataAvailability,
  selection,
}) => {
  const refinedSelection = isEmpty(currentDataquery)
    ? computeDefaultSelection({
        hiddenValuesAnnotation,
        constraints,
        highlightedContraints,
        contentConstraints,
        hasDataAvailability,
        dimensions,
        selection,
        hierarchies,
      })
    : parseDataquery(currentDataquery, dimensions);

  const withAutomated = selectAutomatedSelections(
    refinedSelection,
    dimensions,
    automatedSelections,
    hierarchies,
  );
  return computeDataquery(dimensions, withAutomated);
};
